import { useState, useEffect, useCallback, useRef } from "react";
import isEqual from "lodash/isEqual";
import { getUserPreference, saveUserPreference } from "../services/userPreferenceService";
import { useDebounceCallback } from "./useDebounceCallback";

const DEFAULT_SAVE_DEBOUNCE_MS = 500;

export const PREFERENCE_TYPES = {
  QUICK_FILTER: "QuickFilter",
  SORTING: "Sorting",
  COLUMN_VISIBILITY: "ColumnVisibility",
  COLUMN_LAYOUT: "ColumnLayout",
  PANEL_CONTEXT: "PanelContext",
  PAGINATION: "Pagination",
  PAGE_SIZE: "PageSize",
};

/**
 * Hook for managing user preferences (filters, column visibility, page settings)
 * @param {Object} options
 * @param {string} options.pageKey - Unique identifier for the page (e.g. "schedule_list")
 * @param {string} [options.typeKey="QuickFilter"] - Preference type key
 * @param {any} [options.defaultState=null] - Default state to fall back to
 * @param {Function} [options.onApply=null] - Callback invoked with the loaded or applied state
 */
export const useUserPreference = ({
  pageKey,
  typeKey = PREFERENCE_TYPES.QUICK_FILTER,
  defaultState = null,
  onApply = null,
  // How long to wait after the last change before actually writing to the server. Callers that
  // fire savePreference rapidly (column resize/reorder drags, filter typing) collapse into a
  // single network call instead of one per tick.
  debounceMs = DEFAULT_SAVE_DEBOUNCE_MS,
}) => {
  const [preferenceState, setPreferenceState] = useState(defaultState);
  const [isPreferenceLoading, setIsPreferenceLoading] = useState(false);

  // Use refs for callbacks and defaults so they don't trigger re-renders or infinite loops
  const onApplyRef = useRef(onApply);
  const defaultStateRef = useRef(defaultState);
  const pageKeyRef = useRef(pageKey);
  const typeKeyRef = useRef(typeKey);
  // What we believe is currently persisted server-side, so a save that doesn't actually change
  // anything (e.g. a resize that ends back where it started) can skip the network call entirely.
  const lastPersistedRef = useRef(defaultState);

  useEffect(() => {
    onApplyRef.current = onApply;
    defaultStateRef.current = defaultState;
  }, [onApply, defaultState]);

  useEffect(() => {
    pageKeyRef.current = pageKey;
    typeKeyRef.current = typeKey;
  }, [pageKey, typeKey]);

  // Load preference from API - only depends on pageKey and typeKey
  const loadPreference = useCallback(async () => {
    if (!pageKey) return null;
    setIsPreferenceLoading(true);
    try {
      const saved = await getUserPreference(pageKey, typeKey);
      const effectiveState = saved !== null ? saved : defaultStateRef.current;
      setPreferenceState(effectiveState);
      lastPersistedRef.current = saved;
      if (onApplyRef.current && typeof onApplyRef.current === "function") {
        onApplyRef.current(effectiveState);
      }
      return effectiveState;
    } catch (error) {
      console.error("Error loading preference:", error);
      setPreferenceState(defaultStateRef.current);
      return defaultStateRef.current;
    } finally {
      setIsPreferenceLoading(false);
    }
  }, [pageKey, typeKey]);

  // Load on mount or when pageKey/typeKey changes
  useEffect(() => {
    loadPreference();
  }, [loadPreference]);

  // The actual network write. Stable identity (reads pageKey/typeKey from refs) so
  // useDebounceCallback keeps one running debounce timer instead of resetting it every render.
  const persistToServer = useCallback(async (value) => {
    const key = pageKeyRef.current;
    if (!key) return;
    try {
      await saveUserPreference(key, value, typeKeyRef.current);
      lastPersistedRef.current = value;
    } catch (error) {
      console.error("Error saving preference:", error);
    }
  }, []);

  const debouncedPersist = useDebounceCallback(persistToServer, debounceMs);

  // Save preference: local state updates immediately for a responsive UI; the network write is
  // debounced and skipped entirely when the value hasn't actually changed.
  const savePreference = useCallback(
    (newState) => {
      setPreferenceState(newState);
      if (onApplyRef.current && typeof onApplyRef.current === "function") {
        onApplyRef.current(newState);
      }
      if (!pageKeyRef.current) return newState;
      if (isEqual(newState, lastPersistedRef.current)) {
        debouncedPersist.cancel();
        return newState;
      }
      debouncedPersist(newState);
      return newState;
    },
    [debouncedPersist]
  );

  // Reset preference to defaultState. This is a deliberate one-off action (not a rapid
  // drag/typing stream), so it bypasses the debounce and lands immediately.
  const resetPreference = useCallback(async () => {
    const fallback = defaultStateRef.current;
    setPreferenceState(fallback);
    if (onApplyRef.current && typeof onApplyRef.current === "function") {
      onApplyRef.current(fallback);
    }
    if (!pageKeyRef.current) return fallback;
    debouncedPersist.cancel();
    await persistToServer(fallback);
    return fallback;
  }, [debouncedPersist, persistToServer]);

  // Flush any pending debounced save on unmount so navigating away mid-debounce-window (e.g.
  // right after a resize) doesn't silently drop the last change.
  useEffect(() => {
    return () => {
      debouncedPersist.flush();
    };
  }, [debouncedPersist]);

  return {
    preferenceState,
    setPreferenceState,
    isPreferenceLoading,
    loadPreference,
    savePreference,
    resetPreference,
  };
};

/**
 * Convenience hook for QuickFilter preferences
 */
export const useQuickFilterPreference = ({ pageKey, defaultState = null, onApply = null }) => {
  return useUserPreference({
    pageKey,
    typeKey: PREFERENCE_TYPES.QUICK_FILTER,
    defaultState,
    onApply,
  });
};

/**
 * Convenience hook for Sorting preferences
 */
export const useSortingPreference = ({ pageKey, defaultState = null, onApply = null }) => {
  return useUserPreference({
    pageKey,
    typeKey: PREFERENCE_TYPES.SORTING,
    defaultState,
    onApply,
  });
};

/**
 * Convenience hook for ColumnVisibility preferences
 */
export const useColumnVisibilityPreference = ({ pageKey, defaultState = null, onApply = null }) => {
  return useUserPreference({
    pageKey,
    typeKey: PREFERENCE_TYPES.COLUMN_VISIBILITY,
    defaultState,
    onApply,
  });
};

/**
 * Convenience hook for ColumnLayout preferences (width sizing and column ordering)
 */
export const useColumnLayoutPreference = ({ pageKey, defaultState = null, onApply = null }) => {
  return useUserPreference({
    pageKey,
    typeKey: PREFERENCE_TYPES.COLUMN_LAYOUT,
    defaultState,
    onApply,
  });
};

export default useUserPreference;
